import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Palette, Pencil, BookOpen, ArrowLeft } from "lucide-react";
import { KOLAM_FACTS } from "../data/studio";
import TraceKolam from "../components/studio/TraceKolam";
import ColorKolam from "../components/studio/ColorKolam";
import StudioShell from "../components/studio/StudioShell";
import { useStamp, StampToast } from "../components/studio/Passport";
import { KolamGrid, KolamLoop } from "../components/Motifs";

const STEPS = [
  { id: "learn", label: "1. Learn", icon: BookOpen },
  { id: "trace", label: "2. Trace", icon: Pencil },
  { id: "color", label: "3. Colour", icon: Palette },
];

export default function KolamStudio() {
  const [step, setStep] = useState("learn");
  const { earn, showToast, dismiss } = useStamp("kolam");

  return (
    <StudioShell>
      {/* Hero */}
      <section className="relative overflow-hidden pt-12 pb-8 sm:pt-16 sm:pb-12">
        <div className="absolute top-12 right-4 w-56 h-56 text-[#D95D39] opacity-20 pointer-events-none kolam-bg">
          <KolamGrid cols={7} rows={7} className="w-full h-full" />
        </div>
        <div className="absolute -bottom-8 left-4 w-44 h-44 text-[#1E2A4F] opacity-10 pointer-events-none">
          <KolamLoop className="w-full h-full" />
        </div>
        <div className="max-w-6xl mx-auto px-6 sm:px-10 relative text-center">
          <span className="inline-flex items-center gap-2 text-[#D95D39]">
            <Sparkles size={18} />
            <span className="overline">Module 1 · Tamil Nadu</span>
          </span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#1E2A4F] mt-5 leading-[0.95] tracking-tight">
            Make your own
            <br />
            <em className="italic font-light">magic kolam.</em>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
            Learn what a kolam is, trace one with your finger, colour it in, and save your
            artwork to earn your first Art Passport stamp.
          </p>
        </div>
      </section>

      {/* Step picker */}
      <nav className="max-w-6xl mx-auto px-4 sm:px-10" data-testid="kolam-steps">
        <div className="grid grid-cols-3 gap-2 sm:gap-4 p-2 sm:p-3 rounded-full bg-white border border-[#E5DFD3] shadow-sm">
          {STEPS.map((s) => {
            const Icon = s.icon;
            const active = step === s.id;
            return (
              <button
                key={s.id}
                data-testid={`kolam-step-${s.id}`}
                onClick={() => setStep(s.id)}
                className={`flex items-center justify-center gap-2 px-3 sm:px-5 py-3 rounded-full text-xs sm:text-sm tracking-wide transition-all duration-500 ${
                  active ? "bg-[#1E2A4F] text-[#F9F6F0]" : "text-[#5C6A8F] hover:text-[#1E2A4F]"
                }`}
              >
                <Icon size={16} />
                <span className="hidden sm:inline">{s.label}</span>
                <span className="sm:hidden">{s.label.split(". ")[1]}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* Step content */}
      <section className="max-w-6xl mx-auto px-6 sm:px-10 py-10 sm:py-16 relative">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4 }}
          >
            {step === "learn" && <LearnSection />}
            {step === "trace" && (
              <Section title="Trace a Kolam" subtitle="Follow the faint dotted curves to trace your first kolam. Lift your finger only when you're done.">
                <TraceKolam onSaved={earn} />
              </Section>
            )}
            {step === "color" && (
              <Section title="Colour a Kolam" subtitle="Pick a colour and tap any petal or dot to fill it in. When you save, your stamp is earned.">
                <ColorKolam onSaved={earn} />
              </Section>
            )}
          </motion.div>
        </AnimatePresence>

        <div className="mt-12 flex items-center justify-between flex-wrap gap-3">
          {step !== "learn" && (
            <button
              data-testid="kolam-prev"
              onClick={() => setStep(step === "color" ? "trace" : "learn")}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
            >
              <ArrowLeft size={14} /> Previous
            </button>
          )}
          {step !== "color" && (
            <button
              data-testid="kolam-next"
              onClick={() => setStep(step === "learn" ? "trace" : "color")}
              className="ml-auto inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D95D39] text-[#F9F6F0] hover:bg-[#1E2A4F] transition-colors duration-300 text-sm font-medium tracking-wide"
            >
              {step === "learn" ? "Try tracing →" : "Try colouring →"}
            </button>
          )}
        </div>
      </section>

      <StampToast show={showToast} artName="Kolam" onDismiss={dismiss} />
    </StudioShell>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">{title}</h2>
        <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">{subtitle}</p>
      </div>
      {children}
    </div>
  );
}

function LearnSection() {
  return (
    <div>
      <div className="text-center mb-10">
        <h2 className="font-display text-4xl sm:text-5xl text-[#1E2A4F] leading-tight">
          The story of the kolam.
        </h2>
        <p className="mt-3 text-[#5C6A8F] max-w-2xl mx-auto leading-relaxed">
          Before you draw one, let&apos;s hear how it began. Tap each card to learn a little more.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        {KOLAM_FACTS.map((f, i) => (
          <motion.div
            key={f.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.08 }}
            data-testid={`fact-${i}`}
            className="p-6 sm:p-7 rounded-2xl bg-white border border-[#E5DFD3] hover:border-[#D95D39] transition-colors duration-500"
          >
            <span className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#F3EDE4] text-2xl">
              {f.emoji}
            </span>
            <h3 className="font-display text-2xl text-[#1E2A4F] mt-4">{f.title}</h3>
            <p className="text-[15px] text-[#5C6A8F] leading-relaxed mt-2">{f.body}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-10 p-6 sm:p-8 rounded-2xl bg-[#1E2A4F] text-[#F9F6F0] text-center">
        <p className="overline !text-[#E76F51]">A note from Meera</p>
        <p className="font-display italic text-xl sm:text-2xl mt-3 leading-snug max-w-2xl mx-auto">
          &ldquo;Don&apos;t worry about making it perfect. The best kolams are the ones drawn slowly,
          with a little smile.&rdquo;
        </p>
      </div>
    </div>
  );
}
